// ── participant-recordings-collection: session-scoped recording rows ────────

import { useMemo } from "react";
import {
  Badge,
  useCollection,
  type CollectionColumnDef,
  type CollectionFilterDef,
  type UseCollectionResult
} from "@vjal/ui";

import type { CoachingSessionDto } from "../../services/coaching-dtos";
import { sessionDisplayTitle } from "../sessions/sessions-helpers";
import { cohortSessionFilterInitialState, COHORT_TAB_PAGE_SIZE } from "../cohorts/cohort-detail/cohort-tab-collection-shared";

export type ParticipantRecordingRow = {
  id: string;
  session_id: string;
  type: string;
  access: string;
  passcode: string;
  fetchedAt: string;
} & Record<string, unknown>;

export function mapParticipantRecordingEntries(
  entries: { id: string; recordingType: string; accessPolicy: string; passcodePresent: boolean; fetchedAt: string }[],
  sessionId: string
): ParticipantRecordingRow[] {
  return entries.map((entry) => ({
    id: entry.id,
    session_id: sessionId,
    type: entry.recordingType,
    access: entry.accessPolicy,
    passcode: entry.passcodePresent ? "passcode set" : "no passcode",
    fetchedAt: entry.fetchedAt
  }));
}

export function buildParticipantRecordingsFilterDefs(
  sessions: CoachingSessionDto[]
): CollectionFilterDef<ParticipantRecordingRow>[] {
  return [
    {
      id: "session_id",
      label: "Session",
      type: "select",
      placeholder: "Select session",
      getValue: (row) => row.session_id ?? "",
      options: sessions.map((session) => ({
        value: session.id,
        label: sessionDisplayTitle(session.title, session.id)
      }))
    }
  ];
}

export function buildParticipantRecordingsCollectionColumns(): CollectionColumnDef<ParticipantRecordingRow>[] {
  return [
    { id: "type", header: "Type", priority: 1, cell: (row) => row.type },
    { id: "access", header: "Access policy", priority: 2, cell: (row) => row.access },
    {
      id: "passcode",
      header: "Passcode",
      priority: 2,
      cell: (row) => <Badge variant={row.passcode === "passcode set" ? "info" : "default"}>{row.passcode}</Badge>
    },
    { id: "fetchedAt", header: "Fetched", priority: 3, cell: (row) => row.fetchedAt }
  ];
}

export function useParticipantRecordingsCollection(
  rows: ParticipantRecordingRow[],
  sessions: CoachingSessionDto[],
  activeSessionId: string
): UseCollectionResult<ParticipantRecordingRow> {
  const filters = useMemo(() => buildParticipantRecordingsFilterDefs(sessions), [sessions]);
  const columns = useMemo(() => buildParticipantRecordingsCollectionColumns(), []);
  const initialState = useMemo(
    () => cohortSessionFilterInitialState(activeSessionId),
    [activeSessionId]
  );

  return useCollection<ParticipantRecordingRow>({
    mode: "client",
    rows,
    columns,
    filters,
    pageSize: COHORT_TAB_PAGE_SIZE,
    urlSync: false,
    initialState,
    getRowKey: (row) => row.id
  });
}